import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ProfileCompleteContext } from "../context/ProfileComplete";
import { getMongooseUserDetails } from "../controllers/accounts/mastodon_accounts";

export const useProfileComplete = (userDetails) => {
  const navigate = useNavigate();
  const { setCompleteProfile } = useContext(ProfileCompleteContext);
  const [profile, setProfile] = useState(null);
  const [shouldRefetch, setShouldRefetch] = useState(0);

  useEffect(() => {
    console.log('[useProfileComplete] Hook called with userDetails:', userDetails);
    
    // Get auth from localStorage
    const rawUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    console.log('[useProfileComplete] rawUser from localStorage:', rawUser);
    console.log('[useProfileComplete] storedToken from localStorage:', storedToken);
    const auth = rawUser ? JSON.parse(rawUser) : null;
    
    // Only run if user is logged in
    const userId = auth?.id || userDetails?.id;
    const token = storedToken || userDetails?.token;
    
    console.log('[useProfileComplete] userId:', userId, 'token exists:', !!token);
    
    if (!userId || !token || typeof token !== 'string' || token.trim() === '') {
      console.log('[useProfileComplete] Not logged in or missing credentials, skipping');
      return;
    }

    // Fetch MongoDB user profile
    const fetchProfile = async () => {
      try {
        console.log('[useProfileComplete] Fetching profile for userId:', userId);
        const data = await getMongooseUserDetails(token, userId);
        console.log('[useProfileComplete] Profile data received:', data);
        if (data?.user && data.user.length > 0) {
          console.log('[useProfileComplete] Setting profile:', data.user[0]);
          setProfile(data.user[0]);
        } else {
          console.log('[useProfileComplete] No profile data found - treating as incomplete');
          // If no profile exists, set an empty object to trigger the incomplete check
          setProfile({});
        }
      } catch (error) {
        console.error('[useProfileComplete] Error fetching profile:', error);
        // API unavailable locally (missing env) — skip the check, don't redirect
      }
    };

    fetchProfile();
  }, [userDetails, shouldRefetch]);

  useEffect(() => {
    console.log('[useProfileComplete] Profile check effect triggered, profile:', profile);
    if (!profile) {
      console.log('[useProfileComplete] No profile yet, skipping check');
      return;
    }

    const rawUser = localStorage.getItem('user');
    const auth = rawUser ? JSON.parse(rawUser) : null;
    const userId = auth?.id || userDetails?.id;

    // Helper to check if a value is missing, empty string, or empty array
    const isEmpty = (val) => {
      if (val === undefined || val === null) return true;
      if (Array.isArray(val)) return val.length === 0;
      if (typeof val === 'string') return val.trim().length === 0;
      return false;
    };

    // List of required fields
    const requiredFields = [
      'photos',
      'pronouns',
      'gender_identity',
      'sexual_identity',
      'relationship_style',
      'dating_goals',
      'relationship_status',
    ];

    console.log('[useProfileComplete] Checking required fields...');
    requiredFields.forEach(field => {
      console.log(`[useProfileComplete] ${field}:`, profile[field]);
    });

    // Check if any required field is missing or empty
    const incompleteField = requiredFields.find(field => {
      if (field === 'photos') {
        const result = !Array.isArray(profile.photos) || profile.photos.length < 2;
        console.log(`[useProfileComplete] photos check - isIncomplete: ${result}, value:`, profile.photos);
        return result;
      }
      const result = isEmpty(profile[field]);
      console.log(`[useProfileComplete] ${field} check - isEmpty: ${result}, value:`, profile[field]);
      return result;
    });

    const isIncomplete = Boolean(incompleteField);
    
    console.log('[useProfileComplete] isIncomplete:', isIncomplete, 'incompleteField:', incompleteField);
    console.log('[useProfileComplete] Current URL:', window.location.href);
    
    if (isIncomplete) {
      setCompleteProfile(false);
      if (userId && !window.location.href.includes(`/profile/edit/`)) {
        console.log(`[useProfileComplete] Redirecting to /profile/edit/${userId} due to incomplete field:`, incompleteField);
        navigate(`/profile/edit/${userId}`, { replace: true });
      } else {
        console.log('[useProfileComplete] Already on edit page or no userId');
      }
    } else {
      console.log('[useProfileComplete] Profile is complete');
      setCompleteProfile(true);
    }
  }, [profile, navigate, setCompleteProfile]);

  // Function to manually trigger profile re-check
  const recheckProfile = useCallback(() => {
    console.log('[useProfileComplete] Manual recheck triggered');
    setShouldRefetch(prev => prev + 1);
  }, []);

  return { recheckProfile };
};
